import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface KnowledgeBaseFormFieldV2Props {
  icon: LucideIcon;
  title: string;
  description?: string;
  required?: boolean;
  children?: ReactNode;
  fieldId?: string;
  descriptionId?: string;
}

/**
 * One labelled field in the knowledge-base sheet: a tinted icon tile beside a
 * heading, its helper line, and the control itself.
 *
 * The Tessra twin of `KnowledgeBaseFormField`, which is MUI and so renders with
 * light-mode colours whatever the theme — the MUI palette is pinned to
 * `mode: 'light'` and only the v4 tokens follow the `dark` class.
 */
export const KnowledgeBaseFormFieldV2 = ({
  icon: Icon,
  title,
  description,
  required,
  children,
  fieldId,
  descriptionId,
}: KnowledgeBaseFormFieldV2Props) => {
  const { t } = useTranslation('knowledge');

  return (
    <div className="flex gap-md" data-testid="knowledge-base-form-field-v2">
      <span
        data-testid="knowledge-base-form-field-v2-icon"
        aria-hidden="true"
        className="flex size-12 shrink-0 items-center justify-center rounded-sm bg-muted text-foreground dark:bg-surface-raised"
      >
        <Icon className="size-4" />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-sm">
        <div className="flex flex-col gap-2xs">
          <label
            htmlFor={fieldId}
            id={fieldId ? `${fieldId}-label` : undefined}
            className="type-body-lg-strong text-foreground"
          >
            {title}
            {required && (
              <span
                className="ml-2xs text-destructive"
                aria-label={t('knowledgeBases.knowledgeBaseFormField.requiredField')}
              >
                *
              </span>
            )}
          </label>
          {description && (
            <p id={descriptionId} className="type-body-md text-foreground-alt">
              {description}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

KnowledgeBaseFormFieldV2.displayName = 'KnowledgeBaseFormFieldV2';
